import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import './AdminTable.css';

function AdminReviews() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [minRating, setMinRating] = useState('');
  const [maxRating, setMaxRating] = useState('');
  const [pagination, setPagination] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    if (!user || !user.is_admin) {
      navigate('/');
      return;
    }
    fetchReviews();
  }, [user, navigate, currentPage, minRating, maxRating]);

  const fetchReviews = async () => {
    try {
      setLoading(true);
      const params = { page: currentPage, per_page: 20 };
      if (minRating) params.min_rating = minRating;
      if (maxRating) params.max_rating = maxRating;

      const response = await api.get('/api/admin/reviews', { params });
      setReviews(response.data.reviews || []);
      setPagination(response.data.pagination);
    } catch (err) {
      toast.error(err.response?.data?.error || t('admin.reviews.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const deleteReview = async (reviewId) => {
    if (!window.confirm(t('admin.reviews.deleteConfirm'))) return;

    try {
      await api.delete(`/api/admin/reviews/${reviewId}`);
      toast.success(t('admin.reviews.deleteSuccess'));
      fetchReviews();
    } catch (err) {
      toast.error(err.response?.data?.error || t('admin.reviews.failedToDelete'));
    }
  };

  const renderStars = (rating) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <span key={i} className={i <= rating ? 'star filled' : 'star'}>
          ★
        </span>
      );
    }
    return <div className="stars">{stars}</div>;
  };

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <div>
          <h1>{t('admin.reviews.title')}</h1>
          <p>{t('admin.reviews.subtitle')}</p>
        </div>
      </div>

      <div className="filters">
        <div className="filter-group">
          <label>{t('admin.reviews.minRating')}</label>
          <select value={minRating} onChange={(e) => { setMinRating(e.target.value); setCurrentPage(1); }}>
            <option value="">{t('admin.reviews.any')}</option>
            <option value="1">1+</option>
            <option value="2">2+</option>
            <option value="3">3+</option>
            <option value="4">4+</option>
            <option value="5">5</option>
          </select>
        </div>

        <div className="filter-group">
          <label>{t('admin.reviews.maxRating')}</label>
          <select value={maxRating} onChange={(e) => { setMaxRating(e.target.value); setCurrentPage(1); }}>
            <option value="">{t('admin.reviews.any')}</option>
            <option value="1">1</option>
            <option value="2">2</option>
            <option value="3">3</option>
            <option value="4">4</option>
            <option value="5">5</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="loading">{t('admin.reviews.loading')}</div>
      ) : (
        <>
          <div className="data-table">
            <table>
              <thead>
                <tr>
                  <th>{t('admin.reviews.property')}</th>
                  <th>{t('admin.reviews.reviewer')}</th>
                  <th>{t('admin.reviews.host')}</th>
                  <th>{t('admin.reviews.rating')}</th>
                  <th>{t('admin.reviews.comment')}</th>
                  <th>{t('admin.reviews.date')}</th>
                  <th>{t('admin.reviews.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {reviews.map((review) => (
                  <tr key={review.id}>
                    <td>
                      <div className="property-cell">
                        <div>{review.property.title}</div>
                        <div className="sub-text">{review.property.city}</div>
                      </div>
                    </td>
                    <td>
                      <div className="user-cell">
                        <div>{review.reviewer.name}</div>
                        <div className="sub-text">{review.reviewer.email}</div>
                      </div>
                    </td>
                    <td>
                      <div className="user-cell">
                        <div>{review.host.name}</div>
                        <div className="sub-text">{review.host.email}</div>
                      </div>
                    </td>
                    <td>
                      <div className="rating-cell">
                        {renderStars(review.rating)}
                        <div className="rating-number">{review.rating}/5</div>
                      </div>
                    </td>
                    <td>
                      <div className="comment-cell">
                        {review.comment ? (
                          <>
                            {review.comment.substring(0, 100)}
                            {review.comment.length > 100 ? '...' : ''}
                          </>
                        ) : (
                          <span className="no-comment">{t('admin.reviews.noComment')}</span>
                        )}
                      </div>
                    </td>
                    <td>{new Date(review.created_at).toLocaleDateString()}</td>
                    <td>
                      <button
                        onClick={() => deleteReview(review.id)}
                        className="btn-sm btn-danger"
                        title={t('admin.reviews.delete')}
                      >
                        {t('admin.reviews.delete')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {reviews.length === 0 && (
              <div className="no-results">{t('admin.reviews.noReviews')}</div>
            )}
          </div>

          {pagination && pagination.pages > 1 && (
            <div className="pagination">
              <button
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
              >
                {t('common.previous')}
              </button>
              <span>{t('property.page')} {pagination.page} {t('property.of')} {pagination.pages}</span>
              <button
                disabled={currentPage === pagination.pages}
                onClick={() => setCurrentPage(currentPage + 1)}
              >
                {t('common.next')}
              </button>
            </div>
          )}
        </>
      )}

      <style jsx>{`
        .filters {
          display: flex;
          gap: 20px;
          margin-bottom: 20px;
        }

        .filter-group {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .filter-group label {
          font-weight: 500;
          color: #374151;
        }

        .stars {
          display: flex;
          gap: 2px;
        }

        .star {
          color: #d1d5db;
          font-size: 18px;
        }

        .star.filled {
          color: #fbbf24;
        }

        .rating-cell {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 5px;
        }

        .rating-number {
          font-size: 12px;
          color: #6b7280;
          font-weight: 600;
        }

        .comment-cell {
          max-width: 300px;
          line-height: 1.4;
        }

        .no-comment {
          color: #9ca3af;
          font-style: italic;
        }

        .property-cell,
        .user-cell {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .sub-text {
          font-size: 12px;
          color: #6b7280;
        }
      `}</style>
    </div>
  );
}

export default AdminReviews;
